// src/components/reports/MarketTab.jsx
import React, { useState, useMemo } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  BarChart,
  TrendingUp,
  Globe,
  Cpu,
  Target,
  ArrowUp,
  Zap,
} from "lucide-react";
import { formatIndustryLabel } from "../../utils/reportHelpers";

// Field-type registry — each AI benchmark / market-stat key has its own unit
// semantics. The earlier formatPercent() call treated every value as a
// percentage, producing "4% ROI per dollar" (should be "4×"), "114% hours
// saved/year" (should be "114 hrs"), and "27000% Best Sample Size" (should
// be "27,000 businesses"). Values now route through formatStat() which
// dispatches on the unit registered here.
const STAT_FIELDS = {
  // Buyer-facing benchmark fields shipped from AI_BENCHMARKS_2025 + industry
  roi_per_dollar:            { label: "Avg ROI per $1 invested",     unit: "multiplier" },
  hours_saved_per_year:      { label: "Hours saved per year",        unit: "count_hours" },
  hours_saved_per_week:      { label: "Hours saved per week",        unit: "count_hours" },
  smb_adoption_rate_pct:     { label: "SMBs that have adopted AI",   unit: "percent" },
  smb_adoption_pct:          { label: "SMBs that have adopted AI",   unit: "percent" },
  median_payback_months:     { label: "Median payback (months)",     unit: "count_months" },
  avg_tools_per_smb:         { label: "Avg AI tools per SMB",        unit: "count" },
  // Market / adoption stats
  ai_adoption_rate:          { label: "AI Adoption Rate",            unit: "percent" },
  avg_tools_per_business:    { label: "Avg Tools Per Business",      unit: "count" },
  automation_savings_percent:{ label: "Automation Savings",          unit: "percent" },
  growth_rate:               { label: "Market Growth Rate",          unit: "percent" },
  avg_roi_percent:           { label: "Average ROI",                 unit: "percent" },
  time_to_roi_months:        { label: "Time to ROI",                 unit: "count_months" },
  competitor_adoption:       { label: "Competitor Adoption",         unit: "percent" },
  early_adopter_advantage:   { label: "Early Adopter Advantage",     unit: "percent" },
  cost_reduction_potential:  { label: "Cost Reduction Potential",    unit: "percent" },
  revenue_uplift_potential:  { label: "Revenue Uplift Potential",    unit: "percent" },
  market_size:               { label: "Market Size",                 unit: "raw" },
  top_use_case:              { label: "Top Use Case",                unit: "raw" },
  best_tool_name:            { label: "Top-performing tool",         unit: "raw" },
  best_lift_pct:             { label: "Best tool's lift",            unit: "percent" },
  best_sample_size:          { label: "Best tool's sample size",     unit: "count_businesses" },
  adopted_pct:               { label: "Adopted",                     unit: "percent" },
  not_adopted_pct:           { label: "Not yet adopted",             unit: "percent" },
  first_mover_window:        { label: "First-mover window open",     unit: "boolean" },
};

const friendlyLabel = (key) =>
  STAT_FIELDS[key]?.label
  || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Format a benchmark value according to its registered unit. Returns a
// string ready for direct render. Unknown / unregistered keys fall back to
// percent formatting only when the value is plausibly a percent (0–100),
// otherwise raw — protects against the "27000%" leak.
const formatStat = (key, val) => {
  if (val == null) return "—";
  const cfg = STAT_FIELDS[key];
  if (!cfg) {
    // No unit registered. Don't blindly slap a "%" on — that's how the bug
    // shipped originally. Use percent only for plausible percent values.
    if (typeof val === "number" && val >= 0 && val <= 100) return `${Math.round(val)}%`;
    return typeof val === "number" ? val.toLocaleString("en-US") : String(val);
  }
  const num = typeof val === "number" ? val : parseFloat(val);
  switch (cfg.unit) {
    case "percent":
      return typeof num === "number" && !Number.isNaN(num) ? `${Math.round(num)}%` : String(val);
    case "multiplier":
      // 3.7 → "3.7×"
      return typeof num === "number" && !Number.isNaN(num) ? `${num.toFixed(1)}×` : String(val);
    case "count_hours":
      return typeof num === "number" && !Number.isNaN(num) ? `${Math.round(num)} hrs` : String(val);
    case "count_months":
      return typeof num === "number" && !Number.isNaN(num) ? `${Math.round(num)} mo` : String(val);
    case "count_businesses":
      return typeof num === "number" && !Number.isNaN(num) ? `${num.toLocaleString("en-US")} businesses` : String(val);
    case "count":
      return typeof num === "number" && !Number.isNaN(num)
        ? (num % 1 === 0 ? num.toString() : num.toFixed(1))
        : String(val);
    case "boolean":
      return val ? "Yes" : "No";
    case "raw":
      // Arrays render as comma-list; primitives render verbatim.
      if (Array.isArray(val)) return val.slice(0, 3).join(", ");
      return String(val);
    default:
      return String(val);
  }
};

// Predicate — drop fields that don't make sense in the Adoption Stats grid
// (e.g. arrays, free-text, nested industry blocks). They render fine as
// detail text elsewhere but blow up the "X%" stat-card layout.
const isStatCardRenderable = (key, val) => {
  if (val == null) return false;
  if (Array.isArray(val)) return false;
  if (typeof val === "object") return false;
  const cfg = STAT_FIELDS[key];
  if (cfg && (cfg.unit === "raw" || cfg.unit === "boolean")) return false;
  return true;
};

export const MarketTab = ({
  adoptionStats,
  toolAdoptionData,
  filteredTrends,
  industry,
  subVertical,
  regionalData,
  fullReport,
}) => {
  const [showAllTrends, setShowAllTrends] = useState(false);
  const [expandedTrendIndex, setExpandedTrendIndex] = useState(null);

  const aiMaturity = fullReport?.aiMaturityIndex?.velocityScore; // use velocity score (0–100)
  const industryAvgAdoption = adoptionStats?.ai_adoption_rate ?? 35;

  const maturityGap =
    aiMaturity != null ? Math.round(aiMaturity - industryAvgAdoption) : null;

  const hasData =
    (adoptionStats && Object.keys(adoptionStats).length > 0) ||
    (toolAdoptionData && Object.keys(toolAdoptionData).length > 0) ||
    (filteredTrends && filteredTrends.length > 0) ||
    (regionalData && Object.keys(regionalData).length > 0);

  // R40 (Sarah R36e verdict): was rendering the raw enum slug
  // "real_estate" on 4 surfaces of this tab. formatIndustryLabel
  // humanizes + handles sub_vertical overrides (PM operators see
  // "Property Management", not "Real Estate").
  const industryLabel = formatIndustryLabel(industry, "your industry", subVertical);

  const prioritizedTrends = useMemo(() => {
    if (!filteredTrends || filteredTrends.length === 0) return [];
    return [...filteredTrends].sort((a, b) => {
      const aScore = a.urgencyScore ?? 0;
      const bScore = b.urgencyScore ?? 0;
      return bScore - aScore;
    });
  }, [filteredTrends]);

  if (!hasData) {
    return (
      <div className="text-center py-16 text-slate-400 border border-white/10 rounded-xl">
        <BarChart className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p>Market data is being compiled for your industry.</p>
        <p className="text-sm mt-2">
          Check back soon – we update our benchmarks regularly.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with market pulse */}
      <div className="bg-gradient-to-r from-blue-900/20 to-blue-900/20 border border-blue-500/30 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#3b82f6]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1">
                Market Intelligence
              </h3>
              <p className="text-sm text-slate-400">
                See how your {industryLabel} peers are adopting AI — and where you can get ahead
              </p>
            </div>
          </div>
          {aiMaturity != null && (
            <Card className="bg-slate-950/50 border border-white/10">
              <CardContent className="px-4 py-2 flex items-center gap-2">
                <div className="flex flex-col text-right">
                  <span className="text-xs text-slate-400 uppercase tracking-wide">
                    Your AI Readiness Score
                  </span>
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-sm text-slate-300">
                      You: {Math.round(aiMaturity)}
                    </span>
                    <span className="text-xs text-slate-500">|</span>
                    <span className="text-sm text-slate-300">
                      Peers: {Math.round(industryAvgAdoption)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 justify-end mt-1">
                    <ArrowUp className="w-4 h-4 text-[#3b82f6]" />
                    <span className="text-sm font-medium text-[#3b82f6]">
                      {maturityGap > 0 ? "+" : ""}
                      {maturityGap}% vs industry avg
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Adoption stats */}
      {adoptionStats && Object.keys(adoptionStats).length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-[#3b82f6] mb-4 flex items-center gap-2">
            <BarChart className="h-5 w-5" /> AI Adoption in {industryLabel}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(adoptionStats)
              .filter(([key, val]) =>
                !["source", "lastUpdated", "competitor_actions"].includes(key)
                && isStatCardRenderable(key, val)
              )
              .slice(0, 8)
              .map(([key, val]) => (
                <Card key={key} className="bg-slate-900/70 border border-white/10">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-[#3b82f6]">
                      {formatStat(key, val)}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 break-words">
                      {friendlyLabel(key)}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            *Based on data from {adoptionStats.source || "industry reports"} – last
            update {adoptionStats.lastUpdated || "recent"}.
          </p>
        </div>
      )}

      {/* Benchmark snapshot — what 'toolAdoptionData' actually carries.
          The earlier "Popular Tools" header was a misnomer: this prop is
          aiBenchmarks2025 (industry + SMB averages), not tool popularity %.
          Rendering raw keys ("roi_per_dollar", "hours_saved_per_year") as
          tool names with "%" appended produced "27000% Best Sample Size"
          and "4% ROI per dollar" (should be "27,000 businesses" and "4×").
          Now: humanized labels via friendlyLabel + per-field unit dispatch
          via formatStat. Array/object-typed fields skip via the
          isStatCardRenderable filter. */}
      {toolAdoptionData && Object.keys(toolAdoptionData).length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-blue-400 mb-4 flex items-center gap-2">
            <Cpu className="h-5 w-5" /> Benchmark snapshot — {industryLabel}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(toolAdoptionData)
              .filter(([key, val]) => isStatCardRenderable(key, val))
              .slice(0, 8)
              .map(([key, val]) => (
                <Card key={key} className="bg-slate-900/70 border border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-blue-400">
                        {formatStat(key, val)}
                      </div>
                      <Zap className="w-4 h-4 text-[#3b82f6] opacity-60" />
                    </div>
                    <div className="text-sm text-slate-300 mt-1 break-words">
                      {friendlyLabel(key)}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      )}

      {/* Future trends */}
      {prioritizedTrends && prioritizedTrends.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> Trends That Will Impact Your Business
          </h3>
          <div className="space-y-4">
            {(showAllTrends ? prioritizedTrends : prioritizedTrends.slice(0, 3)).map(
              (trend, index) => {
                const keyIndex = index;
                const steps = trend.actionable_steps || [];
                const isExpanded = expandedTrendIndex === keyIndex;

                return (
                  <Card
                    key={`trend-${index}`}
                    className="bg-slate-900/70 border border-white/10"
                  >
                    <CardContent className="p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                        <div>
                          <div className="font-semibold text-white">
                            {trend.title ?? trend.trend}
                          </div>
                          {trend.urgencyScore != null && (
                            <p className="text-[11px] text-slate-500 mt-1">
                              Priority score: {Math.round(trend.urgencyScore)}/100
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {trend.timeline && (
                            <Badge className="bg-purple-500/20 text-purple-400">
                              {trend.timeline}
                            </Badge>
                          )}
                          {trend.time_to_impact && (
                            <Badge className="bg-blue-500/10 text-[#3b82f6] border-blue-500/30">
                              Impact in {trend.time_to_impact}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mb-3">
                        {trend.description ?? trend.impact}
                      </p>
                      {steps.length > 0 && (
                        <div className="bg-slate-950/50 rounded-xl p-3">
                          <p className="text-xs font-semibold text-[#3b82f6] mb-1 flex items-center gap-1">
                            <Zap className="w-3 h-3" /> Action steps
                          </p>
                          <p className="text-sm text-slate-300">
                            {steps[0]}
                          </p>
                          {steps.length > 1 && (
                            <>
                              {isExpanded && (
                                <ul className="mt-2 space-y-1 text-sm text-slate-300 list-disc list-inside">
                                  {steps.slice(1).map((step, idx) => (
                                    <li key={idx}>{step}</li>
                                  ))}
                                </ul>
                              )}
                              <Button
                                variant="link"
                                className="text-xs text-[#3b82f6] p-0 h-auto mt-1"
                                onClick={() =>
                                  setExpandedTrendIndex(
                                    isExpanded ? null : keyIndex
                                  )
                                }
                              >
                                {isExpanded
                                  ? "Hide extra steps"
                                  : `+ ${steps.length - 1} more`}
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              }
            )}
            {prioritizedTrends.length > 3 && (
              <Button
                variant="ghost"
                onClick={() => setShowAllTrends(!showAllTrends)}
                className="text-slate-400"
              >
                {showAllTrends
                  ? "Show fewer trends"
                  : `Show ${prioritizedTrends.length - 3} more trends`}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Competitor actions */}
      {adoptionStats?.competitor_actions?.length > 0 && (
        <div className="bg-amber-900/10 border border-amber-500/20 rounded-2xl p-5">
          <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
            <Target className="w-5 h-5" /> What Your Competitors Are Already Doing
          </h3>
          <ul className="space-y-2">
            {adoptionStats.competitor_actions.map((action, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-slate-300"
              >
                <ArrowUp className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                {action}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Regional data */}
      {regionalData && Object.keys(regionalData).length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-amber-400 mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5" /> Regional AI Adoption
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(regionalData).map(([region, data]) => (
              <Card
                key={region}
                className="bg-slate-900/70 border border-white/10"
              >
                <CardContent className="p-5">
                  <div className="font-semibold text-white mb-2">
                    {region}
                  </div>
                  {typeof data === "object" ? (
                    <div className="space-y-2">
                      {Object.entries(data).map(([k, v]) => (
                        <div
                          key={k}
                          className="flex justify-between items-center text-sm"
                        >
                          <span className="text-slate-400 capitalize">
                            {k.replace(/_/g, " ")}
                          </span>
                          <span className="font-medium text-white">
                            {typeof v === "number" ? `${v}%` : v}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-300 text-sm">{data}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Strategic implication footer */}
      {aiMaturity != null && (
        <div className="border-t border-white/8 pt-6 text-center">
          <p className="text-sm text-slate-400">
            {maturityGap > 0
              ? `You're ahead of roughly ${Math.abs(
                  maturityGap
                )}% of ${industryLabel} peers in AI velocity. Your blueprint focuses on scaling what already works.`
              : maturityGap < 0
              ? `You're behind roughly ${Math.abs(
                  maturityGap
                )}% of ${industryLabel} peers — the first phases of your blueprint are designed to close this gap quickly.`
              : `You're right at the ${industryLabel} average. The plan you have focuses on pulling you into the top quartile over the next 12 months.`}
          </p>
        </div>
      )}
    </div>
  );
};
